<template>
  <div>
    <v-card class="mb-4">
      <v-card-title>
        <v-icon color="primary" class="me-2">mdi-file-upload-outline</v-icon>
        {{ $t('import_data') }}
      </v-card-title>
      <v-card-subtitle>{{ $t('import_intro') }}</v-card-subtitle>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="kind"
              :items="kinds"
              item-text="label"
              item-value="value"
              :label="$t('what_to_import')"
              dense
              outlined
              hide-details
              @change="reset"
            ></v-select>
          </v-col>
          <v-col cols="12" md="5">
            <v-file-input
              v-model="file"
              :label="$t('choose_a_file')"
              accept=".xlsx,.xls,.csv"
              prepend-icon="mdi-paperclip"
              dense
              outlined
              hide-details
              show-size
              @change="onFileChosen"
            ></v-file-input>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-btn
              color="primary"
              class="me-2"
              :loading="isPreviewing"
              :disabled="!file"
              @click="preview"
            >
              <v-icon left>mdi-eye-outline</v-icon>
              {{ $t('check_the_file') }}
            </v-btn>
            <v-btn text :loading="isDownloading" @click="downloadTemplate">
              <v-icon left>mdi-download</v-icon>
              {{ $t('template') }}
            </v-btn>
          </v-col>
        </v-row>

        <p class="text-caption mt-3 mb-0">{{ matchHint }}</p>
      </v-card-text>
    </v-card>

    <!-- what the file would do. Nothing has happened yet -->
    <v-card v-if="rows.length" class="mb-4">
      <v-card-title class="d-flex flex-wrap align-center">
        <span class="me-3">{{ $t('nothing_has_happened_yet') }}</span>
        <v-chip small color="success" outlined class="me-2">
          {{ $t('will_create_n', { count: totals.create }) }}
        </v-chip>
        <v-chip small color="info" outlined class="me-2">
          {{ $t('will_update_n', { count: totals.update }) }}
        </v-chip>
        <v-chip v-if="totals.skip" small color="error" outlined class="me-2">
          {{ $t('will_skip_n', { count: totals.skip }) }}
        </v-chip>
        <v-chip v-if="totals.warnings" small color="warning" outlined>
          {{ $t('n_to_look_at', { count: totals.warnings }) }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-switch
          v-model="onlyProblems"
          :label="$t('only_rows_to_look_at')"
          dense
          hide-details
          class="mt-0 pt-0 me-4"
        ></v-switch>
        <v-btn
          color="primary"
          :loading="isCommitting"
          :disabled="includedCount === 0"
          @click="confirmCommit"
        >
          <v-icon left>mdi-database-import-outline</v-icon>
          {{ $t('import_n_rows', { count: includedCount }) }}
        </v-btn>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="visibleRows"
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
        dense
        class="import-preview"
      >
        <template #[`item.include`]="{ item }">
          <v-simple-checkbox
            :value="!item.excluded && item.action !== 'skip'"
            :disabled="item.action === 'skip'"
            @input="setIncluded(item, $event)"
          ></v-simple-checkbox>
        </template>

        <template #[`item.action`]="{ item }">
          <v-chip x-small :color="actionColour(item.action)" outlined>
            {{ $t('import_action_' + item.action) }}
          </v-chip>
        </template>

        <!-- edited here rather than in the spreadsheet: a school that has to
             reopen the file, fix a cell and upload again usually just imports
             the mistake -->
        <template
          v-for="field in editableFields"
          #[`item.data.${field}`]="{ item }"
        >
          <v-edit-dialog
            :key="field"
            :return-value.sync="item.data[field]"
            large
            :save-text="$t('save')"
            :cancel-text="$t('cancel')"
            @save="revalidate"
          >
            <span :class="{ 'text--disabled': !item.data[field] }">
              {{ item.data[field] || $t('empty') }}
            </span>
            <template #input>
              <v-text-field
                v-model="item.data[field]"
                :label="fieldLabel(field)"
                single-line
                autofocus
              ></v-text-field>
            </template>
          </v-edit-dialog>
        </template>

        <template #[`item.problems`]="{ item }">
          <div v-for="(problem, index) in item.problems" :key="index" class="py-1">
            <v-icon x-small :color="problemColour(problem.level)" class="me-1">
              {{ problemIcon(problem.level) }}
            </v-icon>
            <span class="text-caption">{{ problem.message }}</span>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- passwords for drivers created without one, shown once and never again -->
    <v-card v-if="credentials.length" class="mb-4">
      <v-card-title>
        <v-icon color="warning" class="me-2">mdi-key-outline</v-icon>
        {{ $t('passwords_shown_once') }}
      </v-card-title>
      <v-card-subtitle>{{ $t('passwords_shown_once_hint') }}</v-card-subtitle>
      <v-card-text>
        <v-simple-table dense>
          <template #default>
            <thead>
              <tr>
                <th>{{ $t('Name') }}</th>
                <th>{{ $t('Email') }}</th>
                <th>{{ $t('Password') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in credentials" :key="index">
                <td>{{ row.name }}</td>
                <td>{{ row.email }}</td>
                <td><code>{{ row.password }}</code></td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <v-btn small text class="mt-2" @click="downloadCredentials">
          <v-icon left small>mdi-download</v-icon>
          {{ $t('download_list') }}
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- and the way back -->
    <v-card>
      <v-card-title>
        <v-icon color="primary" class="me-2">mdi-history</v-icon>
        {{ $t('recent_imports') }}
      </v-card-title>
      <v-card-subtitle>{{ $t('recent_imports_hint') }}</v-card-subtitle>
      <v-card-text>
        <vue-element-loading :active="isLoadingBatches" />
        <p v-if="!batches.length" class="mb-0">{{ $t('no_imports_yet') }}</p>
        <v-simple-table v-else dense>
          <template #default>
            <thead>
              <tr>
                <th>{{ $t('when') }}</th>
                <th>{{ $t('what') }}</th>
                <th>{{ $t('file') }}</th>
                <th>{{ $t('result') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="batch in batches" :key="batch.id">
                <td class="text-no-wrap">{{ batch.created_at }}</td>
                <td>{{ $t(batch.kind) }}</td>
                <td>{{ batch.file_name || '-' }}</td>
                <td>
                  {{ $t('created_n_updated_n', {
                    created: batch.created_count,
                    updated: batch.updated_count,
                  }) }}
                  <div v-if="batch.undo_note" class="text-caption warning--text">
                    {{ batch.undo_note }}
                  </div>
                </td>
                <td class="text-end">
                  <v-chip v-if="batch.undone_at" x-small outlined>{{ $t('undone') }}</v-chip>
                  <v-btn
                    v-else
                    small
                    text
                    color="error"
                    :loading="undoing === batch.id"
                    @click="undo(batch)"
                  >
                    <v-icon left small>mdi-undo-variant</v-icon>
                    {{ $t('undo') }}
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
/*
 * Uploading a list, with a look at it first.
 *
 * The importer this replaces did the whole file the moment it was chosen. A
 * school that uploaded its roll a second time got every child twice; a heading
 * spelled differently imported nonsense in silence; and there was no way back
 * from either except deleting a few hundred rows by hand.
 *
 * So the screen is in two halves. The top reads the file and shows what each
 * row would do - created, updated, or skipped and why - and nothing has
 * happened at that point. Cells are editable in place, because a school that
 * has to reopen the spreadsheet, fix one address and upload again generally
 * imports the mistake instead. The bottom is the last few imports with an Undo
 * against each, so pressing the button is a decision that can be reversed
 * rather than one to be afraid of.
 */
import VueElementLoading from "vue-element-loading";
import { saveBlobResponse, exportRowsToCsv } from "@/utils/download";

export default {
  name: "ImportsIndex",
  components: { VueElementLoading },
  data() {
    return {
      kind: "students",
      file: null,
      rows: [],
      totals: { create: 0, update: 0, skip: 0, warnings: 0 },
      fileName: null,
      credentials: [],
      batches: [],
      onlyProblems: false,
      isPreviewing: false,
      isCommitting: false,
      isDownloading: false,
      isLoadingBatches: false,
      undoing: null,
    };
  },
  computed: {
    kinds() {
      return [
        { value: "students", label: this.$t("students") },
        { value: "drivers", label: this.$t("drivers") },
        { value: "stops", label: this.$t("stops") },
        { value: "buses", label: this.$t("Buses") },
      ];
    },
    //what a second upload of the same list matches on, said before it is pressed
    matchHint() {
      return this.$t("import_match_hint_" + this.kind);
    },
    fields() {
      return {
        students: ["parent_email", "parent_name", "name", "student_identification", "notes"],
        drivers: ["name", "email", "tel_number", "password", "bus_license"],
        stops: ["name", "address", "lat", "lng"],
        buses: ["license", "capacity", "driver_email"],
      }[this.kind];
    },
    //a password is set, not corrected, and is left out of the editable columns
    editableFields() {
      return this.fields.filter((field) => field !== "password");
    },
    headers() {
      const headers = [
        { text: "", value: "include", sortable: false, width: 40 },
        { text: this.$t("line"), value: "row", width: 70 },
        { text: this.$t("what_will_happen"), value: "action", width: 110 },
      ];

      this.editableFields.forEach((field) => {
        headers.push({ text: this.fieldLabel(field), value: "data." + field });
      });

      headers.push({ text: this.$t("notes_on_this_row"), value: "problems", sortable: false });

      return headers;
    },
    visibleRows() {
      if (!this.onlyProblems) return this.rows;

      return this.rows.filter((row) =>
        row.problems.some((problem) => problem.level !== "info")
      );
    },
    includedCount() {
      return this.rows.filter((row) => !row.excluded && row.action !== "skip").length;
    },
  },
  mounted() {
    //arrived from the students or drivers screen, which knows which list the
    //school came here to upload
    const kind = this.$route.query.kind;
    if (["students", "drivers", "stops", "buses"].indexOf(kind) !== -1) {
      this.kind = kind;
    }

    this.loadBatches();
  },
  methods: {
    fieldLabel(field) {
      return this.$t("import_field_" + field);
    },
    actionColour(action) {
      return { create: "success", update: "info", skip: "error" }[action] || "";
    },
    problemColour(level) {
      return { error: "error", warning: "warning", info: "info" }[level] || "";
    },
    problemIcon(level) {
      return {
        error: "mdi-alert-circle",
        warning: "mdi-alert",
        info: "mdi-information-outline",
      }[level];
    },
    reset() {
      this.rows = [];
      this.credentials = [];
      this.totals = { create: 0, update: 0, skip: 0, warnings: 0 };
    },
    onFileChosen() {
      this.reset();
    },
    setIncluded(row, included) {
      this.$set(row, "excluded", !included);
    },
    downloadTemplate() {
      this.isDownloading = true;
      axios
        .get("/imports/template", {
          params: { kind: this.kind },
          responseType: "blob",
        })
        .then((response) => saveBlobResponse(response, this.kind + "_template.xlsx"))
        .catch((error) => {
          this.$notifyBlobError(error, this.$t("error_downloading_template"));
        })
        .then(() => {
          this.isDownloading = false;
        });
    },
    preview() {
      if (!this.file) return;

      const form = new FormData();
      form.append("kind", this.kind);
      form.append("file", this.file);

      this.isPreviewing = true;
      this.credentials = [];
      axios
        .post("/imports/preview", form, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          this.rows = (response.data.rows || []).map((row) =>
            Object.assign({}, row, { excluded: false })
          );
          this.totals = response.data.totals || this.totals;
          this.fileName = response.data.file_name;

          if (!this.rows.length) {
            this.$notifyError(
              { response: { data: { message: this.$t("nothing_to_import") } } },
              this.$t("error_reading_file")
            );
          }
        })
        .catch((error) => {
          this.rows = [];
          this.$notifyError(error, this.$t("error_reading_file"));
        })
        .then(() => {
          this.isPreviewing = false;
        });
    },
    /*
     * An edited cell changes what the row would do - a corrected ID may now
     * match an existing student - so the plan is asked for again rather than
     * left showing the answer to the old value.
     */
    revalidate() {
      if (!this.rows.length) return;

      axios
        .post("/imports/revalidate", {
          kind: this.kind,
          rows: this.rows,
        })
        .then((response) => {
          this.applyPlan(response.data);
        })
        .catch(() => {
          //the row keeps the note it had; the server checks again at import
        });
    },
    applyPlan(data) {
      const excluded = {};
      this.rows.forEach((row) => {
        excluded[row.row + "|" + (row.data.student_identification || row.data.email || row.data.name || "")] =
          row.excluded;
      });

      this.rows = (data.rows || []).map((row) => {
        const key = row.row + "|" + (row.data.student_identification || row.data.email || row.data.name || "");
        return Object.assign({}, row, { excluded: !!excluded[key] });
      });
      this.totals = data.totals || this.totals;
    },
    confirmCommit() {
      this.$confirmAction({
        title: this.$t("import_n_rows", { count: this.includedCount }),
        text: this.$t("confirm_import", {
          create: this.totals.create,
          update: this.totals.update,
        }),
        icon: "question",
        confirmText: this.$t("yes"),
      }).then((confirmed) => {
        if (confirmed) this.commit();
      });
    },
    commit() {
      this.isCommitting = true;
      axios
        .post("/imports/commit", {
          kind: this.kind,
          file_name: this.fileName,
          rows: this.rows.filter((row) => !row.excluded && row.action !== "skip"),
        })
        .then((response) => {
          const batch = response.data.batch || {};
          this.$notifySuccess(
            this.$t("import_done", {
              created: batch.created_count,
              updated: batch.updated_count,
            })
          );
          this.credentials = response.data.credentials || [];

          const failed = response.data.failed || [];
          if (failed.length) {
            this.$notifyError(
              { response: { data: { message: this.$t("n_rows_failed", { count: failed.length }) } } },
              this.$t("import_partly_done")
            );
          }

          this.rows = [];
          this.file = null;
          this.loadBatches();
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_importing"));
        })
        .then(() => {
          this.isCommitting = false;
        });
    },
    loadBatches() {
      this.isLoadingBatches = true;
      axios
        .get("/imports/batches")
        .then((response) => {
          this.batches = response.data.batches || [];
        })
        .catch(() => {
          this.batches = [];
        })
        .then(() => {
          this.isLoadingBatches = false;
        });
    },
    undo(batch) {
      this.$confirmAction({
        title: this.$t("undo_this_import"),
        text: this.$t("confirm_undo_import", {
          created: batch.created_count,
          updated: batch.updated_count,
        }),
        icon: "warning",
        confirmText: this.$t("yes"),
      }).then((confirmed) => {
        if (!confirmed) return;

        this.undoing = batch.id;
        axios
          .post("/imports/undo", { batch_id: batch.id })
          .then((response) => {
            this.$notifySuccess((response.data.success || []).join(", "));

            const kept = response.data.kept || [];
            if (kept.length) {
              //named rather than counted: "the stop Main Gate is on a route"
              //is something the school can act on
              this.$notifyError(
                { response: { data: { message: kept.join("; ") } } },
                this.$t("some_rows_were_kept")
              );
            }
            this.loadBatches();
          })
          .catch((error) => {
            this.$notifyError(error, this.$t("error_undoing_import"));
          })
          .then(() => {
            this.undoing = null;
          });
      });
    },
    downloadCredentials() {
      exportRowsToCsv(
        this.credentials,
        [
          { text: this.$t("Name"), value: "name" },
          { text: this.$t("Email"), value: "email" },
          { text: this.$t("Password"), value: "password" },
        ],
        "driver_passwords"
      );
    },
  },
};
</script>

<style scoped>
.import-preview >>> td {
  vertical-align: top;
  padding-top: 6px;
  padding-bottom: 6px;
}
</style>
